import React from "react";
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Button,
} from "@chakra-ui/react";

export interface ConfirmationDialogsProps {
	// Preset warning
	isPresetWarningOpen: boolean;
	// Total-removal warning
	isTotalRemovalWarningOpen: boolean;
	pendingDeleteColumnLabel?: string;
	pendingPresetLabel?: string;

	onPresetWarningCancel: () => void;
	onPresetWarningConfirm: () => void;
	onTotalRemovalCancel: () => void;
	onTotalRemovalConfirm: () => void;
}

/**
 * Two confirmation AlertDialogs co-located in one component:
 *  1. Preset-change warning — fired when applying a preset over a modified config.
 *  2. Total-removal warning — fired when deleting the last response_value cascades
 *     into removing the total column.
 *
 * Both are controlled — the parent owns open state and decides which callback to
 * invoke (apply preset / cascade delete) on confirm.
 */
export const ConfirmationDialogs = ({
	isPresetWarningOpen,
	onPresetWarningConfirm,
	onPresetWarningCancel,
	pendingPresetLabel,
	isTotalRemovalWarningOpen,
	onTotalRemovalConfirm,
	onTotalRemovalCancel,
	pendingDeleteColumnLabel,
}: ConfirmationDialogsProps) => {
	const presetCancelRef = React.useRef<HTMLButtonElement>(null);
	const totalCancelRef = React.useRef<HTMLButtonElement>(null);

	return (
		<>
			<AlertDialog
				isOpen={isPresetWarningOpen}
				leastDestructiveRef={presetCancelRef}
				onClose={onPresetWarningCancel}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Apply preset?
						</AlertDialogHeader>
						<AlertDialogBody>
							Applying{" "}
							<strong>&ldquo;{pendingPresetLabel || "this preset"}&rdquo;</strong> will
							replace your current columns. This cannot be undone.
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={presetCancelRef} onClick={onPresetWarningCancel}>
								Cancel
							</Button>
							<Button
								colorScheme="blue"
								onClick={onPresetWarningConfirm}
								ml={3}
							>
								Apply Preset
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			<AlertDialog
				isOpen={isTotalRemovalWarningOpen}
				leastDestructiveRef={totalCancelRef}
				onClose={onTotalRemovalCancel}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Remove total column?
						</AlertDialogHeader>
						<AlertDialogBody>
							Deleting{" "}
							<strong>&ldquo;{pendingDeleteColumnLabel || "this column"}&rdquo;</strong>{" "}
							will also remove the Total column, as it requires at least one
							Value column.
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={totalCancelRef} onClick={onTotalRemovalCancel}>
								Cancel
							</Button>
							<Button
								colorScheme="red"
								onClick={onTotalRemovalConfirm}
								ml={3}
							>
								Delete Both
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};

export default ConfirmationDialogs;
